(function () {
  'use strict';

  var PEG_R = 2.5, COIN_MAX = 13, CLEARANCE = 6, STEP_MS = 1000 / 60, TAU = Math.PI * 2;
  var LIGHT = -2.2; // Light sits in the upper left, as on the Volet medallion.
  var matterPromise = null;

  function loadMatter(src, fallback) {
    if (window.Matter) return Promise.resolve(window.Matter);
    if (matterPromise) return matterPromise;
    matterPromise = new Promise(function (resolve, reject) {
      var attempt = function (url, isRetry) {
        var s = document.createElement('script');
        s.src = url;
        s.async = true;
        s.onload = function () { resolve(window.Matter); };
        s.onerror = function () {
          if (!isRetry && fallback) attempt(fallback, true);
          else reject(new Error('Matter.js failed to load'));
        };
        document.head.appendChild(s);
      };
      attempt(src, false);
    });
    return matterPromise;
  }

  function fitCanvas(el, ctx) {
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    var box = el.getBoundingClientRect();
    el.width = Math.round(box.width * dpr);
    el.height = Math.round(box.height * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    return { w: box.width, h: box.height };
  }

  function shuffle(a) {
    for (var i = a.length - 1; i > 0; i--) {
      var j = Math.floor(Math.random() * (i + 1));
      var t = a[i]; a[i] = a[j]; a[j] = t;
    }
    return a;
  }

  function init(root) {
    if (root.dataset.voletReady) return;
    root.dataset.voletReady = '1';

    var cfg;
    try {
      cfg = JSON.parse(root.querySelector('[data-volet-config]').textContent);
    } catch (e) { return; }
    if (!cfg.prizes || cfg.prizes.length < 2) return;

    var canvas = root.querySelector('[data-volet-canvas]');
    var confetti = root.querySelector('[data-volet-confetti]');
    var dropBtn = root.querySelector('[data-volet-drop]');
    var codeBtn = root.querySelector('[data-volet-copy]');
    var fineEl = root.querySelector('[data-volet-fine]');
    var trays = root.querySelectorAll('[data-volet-tray]');
    var gate = root.querySelector('[data-volet-gate]');
    var emailInput = root.querySelector('[data-volet-email]');
    var gateBtn = root.querySelector('[data-volet-gate-submit]');
    var gateError = root.querySelector('[data-volet-gate-error]');
    var consentBox = root.querySelector('[data-volet-consent]');
    var ctx = canvas.getContext('2d');
    var cctx = confetti.getContext('2d');

    var designMode = !!(window.Shopify && window.Shopify.designMode);
    var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var storeKey = 'volet_game_' + (root.dataset.sectionId || 'default');
    var store = cfg.frequency === 'once_per_session' ? window.sessionStorage : window.localStorage;

    var n = cfg.prizes.length;
    var rows = Math.max(4, Math.min(9, cfg.rows || 6));
    var mark = (root.dataset.coinLetter || 'V').slice(0, 2);
    var coinStyle = root.dataset.coinStyle || 'star';
    var coinImg = null;
    if (coinStyle === 'image' && root.dataset.coinImage) {
      var im = new Image();
      im.decoding = 'async';
      im.onload = function () { coinImg = im; };
      im.src = root.dataset.coinImage;
    }

    var W = 0, H = 0, coinR = COIN_MAX, trayTop = 0, pegs = [], sparks = [], palette = {};
    var targetIndex = 0, chosenDrop = null, Matter = null;
    var phase = 'loading', pendingDrop = false;

    function read() {
      if (cfg.frequency === 'always' || designMode) return null;
      try { return store.getItem(storeKey); } catch (e) { return null; }
    }
    function write(v) {
      if (cfg.frequency === 'always' || designMode) return;
      try { store.setItem(storeKey, v); } catch (e) {}
    }

    // Canvas can't read custom properties, so mirror them on every layout.
    function readPalette() {
      var cs = getComputedStyle(root);
      var get = function (name, fallback) {
        return cs.getPropertyValue(name).trim() || fallback;
      };
      return {
        peg: get('--vg-peg', '#ffffff'),
        line: get('--vg-line', '#666666'),
        coinLight: get('--vg-coin-light', '#4a4a4a'),
        coinDark: get('--vg-coin-dark', '#101010')
      };
    }

    function layout() {
      var size = fitCanvas(canvas, ctx);
      W = size.w; H = size.h;
      fitCanvas(confetti, cctx);
      palette = readPalette();

      var topY = 28;
      var gap = Math.min(W / (rows + 2), (H - 60) / rows);

      // The coin must pass between two pegs, so its size follows the geometry
      // rather than the other way round. Otherwise a high row count wedges it.
      coinR = Math.max(6, Math.min(COIN_MAX, (gap - 2 * PEG_R - CLEARANCE) / 2));

      pegs = [];
      for (var r = 0; r < rows; r++) {
        var count = r + 2;
        var rowW = (count - 1) * gap;
        for (var i = 0; i < count; i++) {
          pegs.push({ x: W / 2 - rowW / 2 + i * gap, y: topY + r * gap });
        }
      }
      trayTop = Math.min(H - 34, topY + rows * gap + 6);
    }

    function pickPrize() {
      var total = cfg.prizes.reduce(function (s, p) { return s + p.weight; }, 0);
      if (total <= 0) return Math.floor(Math.random() * n);
      var roll = Math.random() * total;
      for (var i = 0; i < n; i++) {
        roll -= cfg.prizes[i].weight;
        if (roll <= 0) return i;
      }
      return n - 1;
    }

    function bucketOf(x) {
      return Math.max(0, Math.min(n - 1, Math.floor(x / (W / n))));
    }

    // Matter runs the simulation only. All drawing is the custom pass below,
    // because Matter.Render draws debug wireframes.
    function buildWorld(startX, startVx) {
      var M = Matter;
      var engine = M.Engine.create();
      var g = engine.gravity || engine.world.gravity;
      g.y = cfg.gravity;

      var bodies = [
        M.Bodies.rectangle(-12, H / 2, 24, H * 2, { isStatic: true, restitution: 0.4 }),
        M.Bodies.rectangle(W + 12, H / 2, 24, H * 2, { isStatic: true, restitution: 0.4 }),
        M.Bodies.rectangle(W / 2, H + 12, W * 2, 24, { isStatic: true, restitution: 0.1 })
      ];
      pegs.forEach(function (p) {
        bodies.push(M.Bodies.circle(p.x, p.y, PEG_R, {
          isStatic: true, restitution: cfg.bounce, friction: 0.02
        }));
      });
      var trayH = H - trayTop;
      for (var i = 1; i < n; i++) {
        bodies.push(M.Bodies.rectangle((W / n) * i, trayTop + trayH / 2, 3, trayH, {
          isStatic: true, restitution: 0.2
        }));
      }
      var coin = M.Bodies.circle(startX, coinR + 2, coinR, {
        restitution: cfg.bounce, friction: 0.01, frictionAir: 0.006, density: 0.005
      });
      M.Body.setVelocity(coin, { x: startVx, y: 0 });
      bodies.push(coin);
      M.Composite.add(engine.world, bodies);
      return { engine: engine, coin: coin };
    }

    // Matter is deterministic under a fixed timestep, so a headless run
    // predicts exactly what the on-screen replay will do.
    function landingFor(startX, startVx) {
      var w = buildWorld(startX, startVx);
      for (var i = 0; i < 1000; i++) {
        Matter.Engine.update(w.engine, STEP_MS);
        if (w.coin.position.y > H - coinR - 6) break;
      }
      return bucketOf(w.coin.position.x);
    }

    // A peg pyramid is binomially distributed, so reaching the outer trays
    // needs horizontal velocity, not just an offset apex position.
    function preroll(target, done) {
      var candidates = [];
      for (var dx = -14; dx <= 14; dx += 7) {
        for (var vx = -3.2; vx <= 3.21; vx += 0.8) {
          candidates.push({ x: W / 2 + dx, vx: vx });
        }
      }
      shuffle(candidates);
      var i = 0;
      (function next() {
        if (i >= candidates.length) { done(null); return; }
        var c = candidates[i++];
        if (landingFor(c.x, c.vx) === target) { done(c); return; }
        requestAnimationFrame(next);
      })();
    }

    function drawStatic() {
      ctx.clearRect(0, 0, W, H);
      ctx.fillStyle = palette.peg;
      pegs.forEach(function (p) {
        ctx.beginPath();
        ctx.arc(p.x, p.y, PEG_R, 0, TAU);
        ctx.fill();
      });
      ctx.strokeStyle = palette.line;
      ctx.lineWidth = 1;
      for (var i = 1; i < n; i++) {
        var x = Math.round((W / n) * i) + 0.5;
        ctx.beginPath();
        ctx.moveTo(x, trayTop);
        ctx.lineTo(x, H);
        ctx.stroke();
      }
    }

    function coinBase() {
      var g = ctx.createRadialGradient(-coinR * 0.45, -coinR * 0.5, 1, 0, 0, coinR * 1.05);
      g.addColorStop(0, palette.coinLight);
      g.addColorStop(1, palette.coinDark);
      ctx.beginPath();
      ctx.arc(0, 0, coinR, 0, TAU);
      ctx.fillStyle = g;
      ctx.fill();
    }

    // Recessed field so the star and rim sit proud of the surface.
    function coinField() {
      ctx.beginPath();
      ctx.arc(0, 0, coinR * 0.8, 0, TAU);
      ctx.fillStyle = 'rgba(0,0,0,.22)';
      ctx.fill();
    }

    function coinRim() {
      var w = Math.max(1, coinR * 0.13);
      ctx.lineWidth = w;
      ctx.beginPath();
      ctx.arc(0, 0, coinR - w / 2, LIGHT - 1.3, LIGHT + 1.3);
      ctx.strokeStyle = 'rgba(255,255,255,.42)';
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(0, 0, coinR - w / 2, LIGHT + 1.3, LIGHT + TAU - 1.3);
      ctx.strokeStyle = 'rgba(0,0,0,.5)';
      ctx.stroke();
    }

    // Nautical star: each of the five points is two facets, shaded by how far
    // the facet's bisector turns away from the light. That's the emboss.
    function coinStar() {
      var R1 = coinR * 0.74, R2 = coinR * 0.3;
      var facet = function (a1, r1, a2, r2, bisector) {
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.lineTo(Math.cos(a1) * r1, Math.sin(a1) * r1);
        ctx.lineTo(Math.cos(a2) * r2, Math.sin(a2) * r2);
        ctx.closePath();
        var lit = Math.cos(bisector - LIGHT);
        ctx.fillStyle = lit > 0
          ? 'rgba(255,255,255,' + (0.10 + lit * 0.30).toFixed(3) + ')'
          : 'rgba(0,0,0,' + (0.10 - lit * 0.34).toFixed(3) + ')';
        ctx.fill();
      };

      ctx.beginPath();
      for (var i = 0; i < 5; i++) {
        var a = -Math.PI / 2 + i * (TAU / 5);
        var inner = a + Math.PI / 5;
        ctx.lineTo(Math.cos(a) * R1, Math.sin(a) * R1);
        ctx.lineTo(Math.cos(inner) * R2, Math.sin(inner) * R2);
      }
      ctx.closePath();
      ctx.fillStyle = 'rgba(255,255,255,.07)';
      ctx.fill();

      for (var j = 0; j < 5; j++) {
        var ang = -Math.PI / 2 + j * (TAU / 5);
        var back = ang - Math.PI / 5, fwd = ang + Math.PI / 5;
        facet(back, R2, ang, R1, (back + ang) / 2);
        facet(ang, R1, fwd, R2, (ang + fwd) / 2);
      }
    }

    function coinLetter() {
      ctx.fillStyle = 'rgba(255,255,255,.72)';
      ctx.font = '700 ' + Math.round(coinR * 1.05) + 'px Georgia, serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(mark, 0, 1);
    }

    function drawCoin(x, y, angle) {
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(angle);
      if (coinImg) {
        ctx.save();
        ctx.beginPath();
        ctx.arc(0, 0, coinR, 0, TAU);
        ctx.clip();
        ctx.drawImage(coinImg, -coinR, -coinR, coinR * 2, coinR * 2);
        ctx.restore();
      } else {
        coinBase();
        coinField();
        if (coinStyle === 'letter') coinLetter();
        else coinStar();
      }
      coinRim();
      ctx.restore();
    }

    function burst() {
      var cw = confetti.clientWidth, ch = confetti.clientHeight;
      var colors = [palette.peg, palette.coinLight, palette.line];
      for (var i = 0; i < 90; i++) {
        sparks.push({
          x: cw / 2 + (Math.random() - 0.5) * 90,
          y: ch * 0.7,
          vx: (Math.random() - 0.5) * 9,
          vy: -Math.random() * 14 - 5,
          w: 3 + Math.random() * 4,
          h: 6 + Math.random() * 7,
          rot: Math.random() * Math.PI,
          spin: (Math.random() - 0.5) * 0.3,
          life: 1,
          color: colors[(Math.random() * colors.length) | 0]
        });
      }
      (function loop() {
        cctx.clearRect(0, 0, cw, ch);
        var alive = false;
        for (var i = 0; i < sparks.length; i++) {
          var s = sparks[i];
          if (s.life <= 0) continue;
          alive = true;
          s.vy += 0.34; s.vx *= 0.99;
          s.x += s.vx; s.y += s.vy;
          s.rot += s.spin; s.life -= 0.011;
          cctx.save();
          cctx.translate(s.x, s.y);
          cctx.rotate(s.rot);
          cctx.globalAlpha = Math.max(s.life, 0);
          cctx.fillStyle = s.color;
          cctx.fillRect(-s.w / 2, -s.h / 2, s.w, s.h);
          cctx.restore();
        }
        if (alive) requestAnimationFrame(loop);
        else cctx.clearRect(0, 0, cw, ch);
      })();
    }

    function reveal(index) {
      var prize = cfg.prizes[index];
      var won = !!prize.code;
      if (trays[index]) trays[index].classList.add('is-won');

      root.querySelector('[data-volet-won]').textContent =
        won ? 'You won ' + prize.label : (cfg.losingText || 'Not this time.');
      codeBtn.hidden = !won;
      if (fineEl) fineEl.hidden = !won;
      if (won) root.querySelector('[data-volet-code-text]').textContent = prize.code;

      root.querySelector('[data-volet-result]').hidden = false;
      dropBtn.hidden = true;
      if (won && !reduceMotion) burst();
      write(won ? prize.code : 'none');
    }

    function play() {
      var w = buildWorld(chosenDrop.x, chosenDrop.vx);
      var steps = 0;
      (function frame() {
        Matter.Engine.update(w.engine, STEP_MS);
        drawStatic();
        drawCoin(w.coin.position.x, w.coin.position.y, w.coin.angle);
        steps++;
        var v = w.coin.velocity;
        var settled = w.coin.position.y > H - coinR - 10 && Math.hypot(v.x, v.y) < 0.4;
        if (settled || steps > 1000) reveal(bucketOf(w.coin.position.x));
        else requestAnimationFrame(frame);
      })();
    }

    function drop() {
      dropBtn.disabled = true;
      if (reduceMotion || phase === 'failed') { reveal(targetIndex); return; }
      // Tapped before the pre-roll finished, so queue it rather than
      // falling back to a driftless reveal.
      if (phase !== 'ready') { pendingDrop = true; return; }
      play();
    }

    function gateFail(message) {
      if (!gateError) return;
      gateError.textContent = message;
      gateError.hidden = false;
    }

    function unlock() {
      if (gate) gate.hidden = true;
      dropBtn.hidden = false;
      dropBtn.focus({ preventScroll: true });
    }

    // Same endpoint the theme's own newsletter form posts to, so the customer
    // lands in Shopify with marketing consent recorded.
    function postEmail(email) {
      var body = new URLSearchParams();
      body.set('form_type', 'customer');
      body.set('utf8', '\u2713');
      body.set('contact[email]', email);
      body.set('contact[tags]', cfg.newsletterTag || 'luck-popup');
      return fetch('/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString()
      });
    }

    function submitEmail() {
      var email = (emailInput.value || '').trim();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
        gateFail('Enter a valid email address.');
        emailInput.focus();
        return;
      }
      if (consentBox && !consentBox.checked) {
        gateFail('Please accept to continue.');
        return;
      }
      if (gateError) gateError.hidden = true;
      gateBtn.disabled = true;
      gateBtn.textContent = 'One moment';
      // A failed signup must never cost the visitor their drop.
      postEmail(email).catch(function () {}).then(unlock);
    }

    function open() {
      root.hidden = false;
      lockScroll(true);
      layout();
      drawStatic();
      drawCoin(W / 2, coinR + 2, 0);
      requestAnimationFrame(function () { root.classList.add('is-open'); });
      (gate ? emailInput : dropBtn).focus({ preventScroll: true });

      if (reduceMotion) return;
      targetIndex = pickPrize();
      loadMatter(root.dataset.matterSrc, root.dataset.matterFallback).then(function (M) {
        Matter = M;
        // Runs while the visitor reads and types, so the tap is instant.
        preroll(targetIndex, function (found) {
          // No trajectory reaches the weighted pick, so honour the physics and
          // award whichever tray this drop actually lands in.
          chosenDrop = found || { x: W / 2, vx: 0 };
          phase = 'ready';
          if (pendingDrop) { pendingDrop = false; play(); }
        });
      }).catch(function () {
        phase = 'failed';
        if (pendingDrop) { pendingDrop = false; reveal(targetIndex); }
      });
    }

    function lockScroll(on) {
      document.documentElement.style.overflow = on ? 'hidden' : '';
      document.body.style.overflow = on ? 'hidden' : '';
    }

    function close() {
      root.classList.remove('is-open');
      lockScroll(false);
      setTimeout(function () { root.hidden = true; }, 520);
      if (!read()) write('dismissed');
    }

    dropBtn.addEventListener('click', drop);
    root.querySelector('[data-volet-close]').addEventListener('click', close);
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !root.hidden) close();
    });

    if (gateBtn) {
      gateBtn.addEventListener('click', submitEmail);
      emailInput.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') { e.preventDefault(); submitEmail(); }
      });
    }

    codeBtn.addEventListener('click', function () {
      var code = root.querySelector('[data-volet-code-text]').textContent;
      var label = root.querySelector('[data-volet-copy-label]');
      var original = label.dataset.original || label.textContent;
      label.dataset.original = original;
      var done = function () {
        label.textContent = 'Copied';
        setTimeout(function () { label.textContent = original; }, 1600);
      };
      if (navigator.clipboard) navigator.clipboard.writeText(code).then(done, done);
      else done();
    });

    window.addEventListener('resize', function () {
      if (root.hidden) return;
      layout();
      drawStatic();
    });

    if (!read()) setTimeout(open, cfg.delay);
  }

  function scan(scope) {
    (scope || document).querySelectorAll('[data-volet-game]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { scan(); });
  } else {
    scan();
  }

  document.addEventListener('shopify:section:load', function (e) { scan(e.target); });
})();